// src/pages/sessions/components/LiveSession/hooks/useSessionInitialization.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import sessionService from '@/services/sessionService';
import { toast } from 'sonner';

export const useSessionInitialization = (sessionId, user, isAuthenticated, isTeacher) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [waitingForTeacher, setWaitingForTeacher] = useState(false);
  const [preloadedRoomAssignment, setPreloadedRoomAssignment] = useState(null);
  const [preloadedRooms, setPreloadedRooms] = useState(null);
  
  const initializationRef = useRef(false);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  // Fix: Handle isTeacher as either a function or boolean
  const isTeacherUser = typeof isTeacher === 'function' ? isTeacher() : isTeacher;

  const extractToken = useCallback((response) => {
    if (!response) {
      console.error('extractToken: No response provided');
      return null;
    }
    
    // Check all possible locations where token might be
    const candidates = [
      response?.data?.data?.token,  // Backend returns { data: { data: { token } } }
      response?.data?.token,         // Backend returns { data: { token } }
      response?.token,              // Backend returns { token }
      response?.data,               // If data itself is the token string
    ];

    console.log('extractToken: Checking candidates:', {
      'data.data.token': response?.data?.data?.token ? 'EXISTS' : 'NULL',
      'data.token': response?.data?.token ? 'EXISTS' : 'NULL',
      'token': response?.token ? 'EXISTS' : 'NULL',
      'data': typeof response?.data === 'string' ? 'STRING' : typeof response?.data
    });

    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      if (typeof candidate === 'string' && candidate.length > 20) {
        console.log(`Token found at position ${i}, length: ${candidate.length}`);
        return candidate;
      }
    }
    
    console.error('No valid token found in response structure:', response);
    return null;
  }, []);

  const initializeSession = useCallback(async () => {
    if (initializationRef.current || !sessionId || !isAuthenticated || !user || !mountedRef.current) {
      return;
    }

    // Cancel any existing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    initializationRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Get session details with abort signal
      const sessionResponse = await sessionService.getSessionById(sessionId, {
        signal: abortController.signal
      });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      
      if (!sessionResponse?.success) {
        throw new Error('Failed to get session details');
      }

      const session = sessionResponse.data;
      setSessionData(session);

      const status = session.status || session.session_status;

      if (status === 'live') {
        let virtualRoomId = null;
        
        // For students, preload room assignment BEFORE getting token
        if (!isTeacherUser && user?.institutionMemberId) {
          try {
            // Load rooms and assignment in parallel with abort signals
            const [roomsRes, assignmentRes] = await Promise.all([
              sessionService.getBreakoutRooms(sessionId, {
                signal: abortController.signal
              }),
              sessionService.getUserRoomAssignment(sessionId, {
                signal: abortController.signal
              })
            ]);
            
            // Check mounted after async operation
            if (!mountedRef.current) return;
            
            if (roomsRes?.success && roomsRes?.data) {
              setPreloadedRooms(roomsRes.data);
              console.log('Loaded breakout rooms:', roomsRes.data.length);
            }
            
            if (assignmentRes?.success && assignmentRes?.data?.virtualRoomId) {
              virtualRoomId = assignmentRes.data.virtualRoomId;
              setPreloadedRoomAssignment(virtualRoomId);
              console.log('Preloaded room assignment:', virtualRoomId);
            } else {
              console.log('No room assignment found for student');
            }
          } catch (err) {
            // Check if error is from abort
            if (err.name === 'AbortError') {
              console.log('Room data loading aborted');
              return;
            }
            
            // Check mounted after catch
            if (!mountedRef.current) return;
            
            console.error('Failed to preload room data:', err);
            // Continue anyway - student will be in main room
          }
        }
        
        console.log('Requesting token with virtualRoomId:', virtualRoomId);
        
        // Get token with room assignment if available
        const tokenResponse = await sessionService.getLiveKitToken(
          sessionId, 
          'main',
          virtualRoomId, // Pass the room assignment to the backend
          { signal: abortController.signal }
        );
        
        // Check mounted after async operation
        if (!mountedRef.current) return;
        
        console.log('Raw token response:', tokenResponse);
        console.log('Token response structure:', {
          success: tokenResponse?.success,
          hasData: !!tokenResponse?.data,
          dataType: typeof tokenResponse?.data,
          hasDataData: !!tokenResponse?.data?.data,
          hasToken: !!tokenResponse?.data?.token,
          hasDataToken: !!tokenResponse?.data?.data?.token
        });
        
        const extractedToken = extractToken(tokenResponse);
        
        if (!extractedToken) {
          console.error('Token extraction failed. Full response:', JSON.stringify(tokenResponse, null, 2));
          throw new Error('Invalid token received - token extraction failed');
        }
        
        console.log('Token extracted successfully, length:', extractedToken.length);
        setToken(extractedToken);
        
      } else if (status === 'scheduled') {
        if (isTeacherUser) {
          // Teacher can start
          setLoading(false);
          return;
        } else {
          // Student must wait
          setWaitingForTeacher(true);
        }
      } else {
        throw new Error('Session has ended or been cancelled');
      }
    } catch (error) {
      // Check if error is from abort
      if (error.name === 'AbortError') {
        console.log('Session initialization aborted');
        return;
      }
      
      // Check mounted after catch
      if (!mountedRef.current) return;
      
      console.error('Session initialization failed:', error);
      setError(error.message || 'Failed to initialize session');
    } finally {
      // Check mounted in finally
      if (mountedRef.current) {
        setLoading(false);
        initializationRef.current = false;
      }
    }
  }, [sessionId, isAuthenticated, user, extractToken, isTeacherUser]);

  const handleStartSession = useCallback(async () => {
    if (!isTeacherUser || !mountedRef.current) {
      toast.error('Only teachers can start sessions');
      return;
    }
    
    // Create abort controller for this operation
    const startAbortController = new AbortController();
    
    setLoading(true);
    try {
      const response = await sessionService.startSession(sessionId, {
        signal: startAbortController.signal
      });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      
      if (!response?.success) {
        console.error('Start session failed:', response);
        throw new Error(response?.message || 'Failed to start session');
      }
      
      toast.success('Session started successfully');
      
      // Reinitialize to get token
      initializationRef.current = false;
      await initializeSession();
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      
    } catch (error) {
      // Check if error is from abort
      if (error.name === 'AbortError') {
        console.log('Start session aborted');
        return;
      }
      
      // Check mounted after catch
      if (!mountedRef.current) return;
      
      console.error('Error starting session:', error);
      toast.error(error.message || 'Failed to start session');
    } finally {
      // Check mounted in finally
      if (mountedRef.current) {
        setLoading(false);
      }
      
      // Clean up abort controller
      startAbortController.abort();
    }
  }, [sessionId, isTeacherUser, initializeSession]);

  // Effect to run initialization
  useEffect(() => {
    mountedRef.current = true;
    
    initializeSession();
    
    return () => {
      mountedRef.current = false;
      
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Reset initialization flag
      initializationRef.current = false;
    };
  }, [initializeSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return {
    token,
    loading,
    error,
    sessionData,
    waitingForTeacher,
    handleStartSession,
    preloadedRoomAssignment,
    preloadedRooms
  };
};